import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';
import { useCredentialsByType } from '../../../../hooks/useCredentials';
import { useCustomerGroups } from '../../../../hooks/useCustomerGroups';
import seemeLogo from '../../../../assets/seemelogo.png';

const SMSSendConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['recipient', 'credentials', 'message']));
  const [useCredential, setUseCredential] = useState(!!node.data.config?.credentialId);
  
  // Provider automatikus detektálás a node típusából
  const provider: 'twilio' | 'seeme' = node.type === 'seeme_sms_send' ? 'seeme' : 'twilio';
  
  // Debug: Ellenőrizzük, hogy a config megfelelően van-e inicializálva
  // React.useEffect(() => {
  //   console.log('SMS Node Type:', node.type);
  //   console.log('SMS Node Config:', node.data.config);
  //   console.log('Auto-detected Provider:', provider);
  // }, []);
  
  // Credentials lekérése a provider alapján
  const { credentials, loading: credentialsLoading, refresh: refreshCredentials } = useCredentialsByType(provider);
  const { groups, loading: groupsLoading } = useCustomerGroups();
  
  // Credentials újratöltése, amikor a provider változik
  React.useEffect(() => {
    // console.log('Provider changed to:', provider);
    refreshCredentials();
  }, [provider]);
  
  // Debug: Credentials lista
  // React.useEffect(() => {
  //   console.log('Credentials loaded:', credentials.length, credentials);
  // }, [credentials]);
  
  // Recipient mode: 'single' = egy címzett, 'group' = ügyfélcsoport
  const recipientMode = node.data.config?.recipientMode || 'single';
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  // Credential használat váltása
  const handleCredentialToggle = (checked: boolean) => {
    setUseCredential(checked);
    if (!checked) {
      updateConfig('credentialId', undefined);
    }
  };

  // Test mode toggle
  const handleTestModeToggle = (checked: boolean) => {
    updateConfig('testMode', checked);
  };

  return (
    <div className="space-y-3">
      {/* Provider info banner */}
      <div className={`rounded-lg p-3 ${provider === 'seeme' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'}`}>
        <p className={`text-xs font-medium ${provider === 'seeme' ? 'text-red-800 dark:text-red-200' : 'text-green-800 dark:text-green-200'}`}>
          {provider === 'seeme' ? (
            <>💬 <strong>Seeme.hu SMS</strong> - Magyar SMS szolgáltatás</>
          ) : (
            <>📱 <strong>Twilio SMS</strong> - Nemzetközi SMS szolgáltatás</>
          )}
        </p>
      </div>

      {/* Címzett Típusa */}
      <CollapsibleSection
        id="recipient"
        title="Címzett Kiválasztása"
        icon="👥"
        color="blue"
        isExpanded={expandedSections.has('recipient')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          {/* Recipient Mode Selector */}
          <div>
            <label className={commonStyles.label}>
              Címzett típusa
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => updateConfig('recipientMode', 'single')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  recipientMode === 'single'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-300'
                }`}
              >
                <div className="text-2xl mb-1">📱</div>
                <div className="font-medium text-sm">Egy címzett</div>
                <div className="text-xs opacity-75 mt-1">Egyedi telefonszám</div>
              </button>
              
              <button
                type="button"
                onClick={() => updateConfig('recipientMode', 'group')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  recipientMode === 'group'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-300'
                }`}
              >
                <div className="text-2xl mb-1">👥</div>
                <div className="font-medium text-sm">Ügyfélcsoport</div>
                <div className="text-xs opacity-75 mt-1">Tömeges küldés</div>
              </button>
            </div>
          </div>

          {/* Ügyfélcsoport választó */}
          {recipientMode === 'group' && (
            <div>
              <label className={commonStyles.label}>
                Válassz ügyfélcsoportot
              </label>
              {groupsLoading ? (
                <div className="text-sm text-gray-500">Csoportok betöltése...</div>
              ) : groups.length === 0 ? (
                <div className="text-sm bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                  <p className="text-yellow-800 dark:text-yellow-200 font-medium mb-2">⚠️ Még nincs ügyfélcsoport</p>
                  <p className="text-yellow-700 dark:text-yellow-300 text-xs">
                    Hozz létre ügyfélcsoportokat az <strong>Ügyfelek</strong> menüben!
                  </p>
                </div>
              ) : (
                <select
                  value={node.data.config?.customerGroupId || ''}
                  onChange={(e) => updateConfig('customerGroupId', e.target.value)}
                  className={commonStyles.select}
                >
                  <option value="">Válassz csoportot...</option>
                  {groups.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.icon || '👥'} {group.name} ({group.customerCount} ügyfél)
                    </option>
                  ))}
                </select>
              )}
              
              {node.data.config?.customerGroupId && (
                <div className="mt-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    💡 <strong>Tömeges küldés:</strong> Az SMS minden ügyfélnek elküldésre kerül a csoportban, akinek van telefonszáma.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Egyedi telefonszám - csak single mode-ban */}
          {recipientMode === 'single' && (
            <div>
              <label className={commonStyles.label}>
                Címzett Telefonszám
              </label>
              <input
                type="text"
                value={node.data.config.to || ''}
                onChange={(e) => updateConfig('to', e.target.value)}
                className={commonStyles.input}
                placeholder="{{customer_phone}} vagy +36301234567"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Címzett telefonszáma (nemzetközi formátumban)
              </p>
            </div>
          )}
        </div>
      </CollapsibleSection>
      {/* Credential Beállítások */}
      <CollapsibleSection
        id="credentials"
        title={`${provider === 'twilio' ? 'Twilio' : 'Seeme.hu'} Beállítások`}
        icon="🔑"
        color="green"
        isExpanded={expandedSections.has('credentials')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          {/* Test Mode Toggle */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md p-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={node.data.config.testMode || false}
                onChange={(e) => handleTestModeToggle(e.target.checked)}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm font-medium text-purple-700 dark:text-purple-300">
                🧪 Test Mode (nem küld valódi SMS-t)
              </span>
            </label>
            <p className="text-xs text-purple-600 dark:text-purple-400 mt-2">
              Test mode-ban nem kell Twilio fiók, csak logol. Tökéletes fejlesztéshez!
            </p>
          </div>

          {/* Credential választó - csak ha NEM test mode */}
          {!node.data.config.testMode && (
            <div>
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={useCredential}
                  onChange={(e) => handleCredentialToggle(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  🔐 Mentett {provider === 'twilio' ? 'Twilio' : 'Seeme.hu'} fiók használata (ajánlott)
                </span>
              </label>

              {useCredential && (
                <div>
                  {credentialsLoading ? (
                    <div className="text-sm text-gray-500">Betöltés...</div>
                  ) : credentials.length === 0 ? (
                    <div className="text-sm bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                      <p className="text-yellow-800 dark:text-yellow-200 font-medium mb-2">
                        ⚠️ Még nincs mentett {provider === 'twilio' ? 'Twilio' : 'Seeme.hu'} fiók
                      </p>
                      <p className="text-yellow-700 dark:text-yellow-300 text-xs">
                        Kattints a fejlécben a <strong>🔑 API Kulcsok</strong> gombra és hozz létre egy {provider === 'twilio' ? 'Twilio' : 'Seeme.hu'} fiókot!
                      </p>
                    </div>
                  ) : (
                    <select
                      value={node.data.config?.credentialId || ''}
                      onChange={(e) => updateConfig('credentialId', e.target.value)}
                      className={commonStyles.select}
                    >
                      <option value="">Válassz {provider === 'twilio' ? 'Twilio' : 'Seeme.hu'} fiókot...</option>
                      {credentials.map(cred => (
                        <option key={cred.id} value={cred.id}>
                          {cred.name} {cred.data.phoneNumber ? `(${cred.data.phoneNumber})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Feladó szám - opcionális, ha nincs credential-ben */}
          {!node.data.config.testMode && !useCredential && (
            <div>
              <label className={commonStyles.label}>
                Feladó Telefonszám (opcionális)
              </label>
              <input
                type="text"
                value={node.data.config.from || ''}
                onChange={(e) => updateConfig('from', e.target.value)}
                className={commonStyles.input}
                placeholder="+36201234567 vagy {{twilio_number}}"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Twilio telefonszám (nemzetközi formátumban). Ha üresen hagyod, a credential-ből veszi.
              </p>
            </div>
          )}

          {!node.data.config.testMode && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
              <div className="text-xs text-blue-700 dark:text-blue-300">
                <strong>💡 Twilio Setup:</strong>
                <ol className="list-decimal list-inside mt-2 space-y-1">
                  <li>Regisztrálj a <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" className="underline">twilio.com</a> oldalon</li>
                  <li>Vásárolj telefonszámot a Console-ban (opcionális kezdéshez)</li>
                  <li>Hozz létre egy Twilio credential-t az API Kulcsok menüben</li>
                  <li>Használd a változókat: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{'{{customer_phone}}'}</code></li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Üzenet Beállítások */}
      <CollapsibleSection
        id="message"
        title="SMS Üzenet"
        icon="💬"
        color="purple"
        isExpanded={expandedSections.has('message')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Üzenet Szövege
            </label>
            <textarea
              value={node.data.config.message || ''}
              onChange={(e) => updateConfig('message', e.target.value)}
              className={commonStyles.textarea}
              rows={4}
              placeholder="Kedves {{customer_name}}! Az Ön rendelése elkészült. Átveheti a {{pickup_location}} címen."
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              SMS üzenet szövege (max 160 karakter javasolt)
            </p>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Karakter: {(node.data.config.message || '').length} / 160
              {(node.data.config.message || '').length > 160 && (
                <span className="text-orange-600 ml-2">⚠️ Több SMS-ként kerül elküldésre</span>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="useTemplate"
              checked={node.data.config.useTemplate || false}
              onChange={(e) => updateConfig('useTemplate', e.target.checked)}
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            />
            <label htmlFor="useTemplate" className="text-sm text-gray-700 dark:text-gray-300">
              Sablon használata
            </label>
          </div>

          {node.data.config.useTemplate && (
            <div>
              <label className={commonStyles.label}>
                Sablon Azonosító
              </label>
              <input
                type="text"
                value={node.data.config.templateId || ''}
                onChange={(e) => updateConfig('templateId', e.target.value)}
                className={commonStyles.input}
                placeholder="order_confirmation"
              />
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Haladó Beállítások */}
      <CollapsibleSection
        id="advanced"
        title="Haladó Beállítások"
        icon="⚙️"
        color="gray"
        isExpanded={expandedSections.has('advanced')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="validateNumber"
              checked={node.data.config.validateNumber !== false}
              onChange={(e) => updateConfig('validateNumber', e.target.checked)}
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            />
            <label htmlFor="validateNumber" className="text-sm text-gray-700 dark:text-gray-300">
              Telefonszám validálás
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="sendAsync"
              checked={node.data.config.sendAsync || false}
              onChange={(e) => updateConfig('sendAsync', e.target.checked)}
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            />
            <label htmlFor="sendAsync" className="text-sm text-gray-700 dark:text-gray-300">
              Aszinkron küldés (ne várjon a válaszra)
            </label>
          </div>

          <div>
            <label className={commonStyles.label}>
              Újrapróbálkozások száma
            </label>
            <input
              type="number"
              value={node.data.config.retryCount || 3}
              onChange={(e) => updateConfig('retryCount', parseInt(e.target.value))}
              className={commonStyles.input}
              min="0"
              max="5"
            />
          </div>

          <div>
            <label className={commonStyles.label}>
              Timeout (másodperc)
            </label>
            <input
              type="number"
              value={node.data.config.timeout || 30}
              onChange={(e) => updateConfig('timeout', parseInt(e.target.value))}
              className={commonStyles.input}
              min="5"
              max="120"
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            Az alábbi változók érhetők el a következő node-okban:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{sms_message_id}}', desc: 'SMS üzenet azonosító', example: 'SM1234567890abcdef' },
              { var: '{{sms_status}}', desc: 'Küldés státusza', example: 'sent' },
              { var: '{{sms_to}}', desc: 'Címzett telefonszám', example: '+36301234567' },
              { var: '{{sms_from}}', desc: 'Feladó telefonszám', example: '+36201234567' },
              { var: '{{sms_sent_at}}', desc: 'Küldés időpontja', example: '2024-01-15T10:30:00Z' },
              { var: '{{sms_segments}}', desc: 'SMS szegmensek száma', example: '1' },
              { var: '{{sms_cost}}', desc: 'Küldés költsége', example: '0.05 USD' }
            ].map(({ var: variable, desc, example }) => (
              <div key={variable} className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded border border-purple-200 dark:border-purple-800">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-700 dark:text-purple-300">{variable}</code>
                  <button
                    onClick={() => navigator.clipboard.writeText(variable)}
                    className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
                  >
                    Másolás
                  </button>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{desc}</div>
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">Példa: {example}</div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Költség Becslés */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
        <div className="text-xs text-yellow-700 dark:text-yellow-300">
          <strong>💰 Költség Információ:</strong>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Twilio SMS ár: ~$0.0075 / SMS (USA)</li>
            <li>Magyarország: ~$0.05 / SMS</li>
            <li>160 karakter felett több SMS-ként számolódik</li>
            <li>Ellenőrizd az aktuális árakat a Twilio Console-ban</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SMSSendConfig;
